import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import type { FC, ReactNode } from 'react';
import { useInView } from 'react-intersection-observer';
import fetchAPI from 'app/helpers/fetch';

export interface Meta {
  page: number;
  per: number;
  total_pages: number;
  total_count: number;
}

export interface PaginatedItems<T> {
  data: T[];
  meta: Meta;
}

// A link to the next page of the list, in the server-rendered HTML too: crawlers don't scroll, so
// they follow it instead, while a click on it loads the next page in place, like scrolling does.
export interface NextPageLink {
  // The page address of the list (path and query string, without `page`).
  path: string;
  text: string;
  className?: string;
}

// The given page of the list at `path`, keeping the rest of its query string.
const pageHref = (path: string, page: number) => {
  const [pathname, search = ''] = path.split('?');
  const query = new URLSearchParams(search);
  query.set('page', page.toString());
  return `${pathname}?${query}`;
};

export const RETRY_PERIOD = 3000;

const moreGamesPresent = (meta: Meta) => !!meta.total_pages && meta.page !== meta.total_pages;

// Without explicit params the next page keeps the address bar's query string.
const nextPageParams = (page: number, params = new URLSearchParams(window.location.search)) => {
  const next = new URLSearchParams(params);
  next.set('page', (page + 1).toString());
  return next.toString();
};

const useInfiniteScroll: <T>(
  url: string,
  initialItems?: PaginatedItems<T>,
  maxAdditionalPages?: number,
  params?: URLSearchParams,
  loginOnUnauthorized?: boolean
) => [() => void, boolean, boolean, boolean, T[], number] = (
  url,
  initialItems,
  maxAdditionalPages,
  params,
  loginOnUnauthorized
) => {
  const [loading, setLoading] = useState(false);
  const [games, setGames] = useState(initialItems?.data || []);
  const [meta, setMeta] = useState(initialItems?.meta);
  const [error, setError] = useState(false);
  const [additionalPagesLoaded, setAdditionalPagesLoaded] = useState(0);
  const belowPageLimit = maxAdditionalPages === undefined || additionalPagesLoaded < maxAdditionalPages;
  const hasMore = (!meta || moreGamesPresent(meta)) && belowPageLimit;
  const timer = useRef<NodeJS.Timeout | undefined>(undefined);

  const load = () => {
    if (loading || !hasMore) return;

    setLoading(true);
    fetchAPI(`${url}.json?${nextPageParams(meta ? meta.page : 0, params)}`, {}, { loginOnUnauthorized })
      .then((newGames) => {
        setGames((g) => [...g, ...newGames.data]);
        setMeta(newGames.meta);
        setAdditionalPagesLoaded((count) => count + 1);
        setLoading(false);
        setError(false);
      })
      .catch(() => {
        setError(true);
        timer.current = setTimeout(load, RETRY_PERIOD);
      });
  };

  useEffect(
    () => () => {
      clearTimeout(timer.current);
    },
    []
  );

  return [load, hasMore, loading, error, games, (meta ? meta.page : 0) + 1];
};

const Error: FC = () => (
  <ul className="alerts error">
    <li className="alert alert-error">Games loading error</li>
  </ul>
);

const ViewTrigger: FC<{ onView: () => void }> = ({ onView }) => {
  const [ref, inView] = useInView({ rootMargin: '0% 0% 50% 0%' });

  useEffect(() => {
    if (inView) {
      onView();
    }
  }, [inView]);

  return <div style={{ padding: 0, margin: 0 }} ref={ref} />;
};

interface InfiniteScrollProps<T> {
  initialItems: PaginatedItems<T>;
  url?: string;
  maxAdditionalPages?: number;
  // The search params of the next pages (defaults to the address bar's query string).
  params?: URLSearchParams;
  nextPageLink?: NextPageLink;
  loginOnUnauthorized?: boolean;
  children: (items: T[]) => ReactNode;
}

const InfiniteScroll = <T,>({
  initialItems,
  url,
  maxAdditionalPages,
  params,
  nextPageLink,
  loginOnUnauthorized,
  children,
}: InfiniteScrollProps<T>): JSX.Element => {
  const [load, hasMore, loading, error, items, nextPage] = useInfiniteScroll(
    url || window.location.pathname,
    initialItems,
    maxAdditionalPages,
    params,
    loginOnUnauthorized
  );

  return (
    <>
      {children(items)}
      {hasMore && (
        <>
          <ViewTrigger onView={load} />
          {/* While the next page loads, the spinner takes the link's place. */}
          {nextPageLink && !loading ? (
            <a
              href={pageHref(nextPageLink.path, nextPage)}
              className={nextPageLink.className}
              onClick={(e) => {
                // A modified or non-primary click opens the page in a new tab or window, as usual.
                if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

                e.preventDefault();
                load();
              }}
            >
              {nextPageLink.text}
            </a>
          ) : (
            <div className="spinner" aria-label="spinner" />
          )}
        </>
      )}
      {error && <Error />}
    </>
  );
};

export default InfiniteScroll;
